import { logger } from '../logging/logger'

type HostCallback = (...args: never[]) => unknown

/**
 * Wraps every function on a host callback object so an integrator's throw stays
 * the integrator's problem.
 *
 * Applied where callbacks enter the SDK rather than at each call site: some of
 * those sites can't defend themselves usefully — `onClose` runs inside a
 * `setState` updater, `onOpen` runs before the sheet is open — and a call site
 * added later would otherwise be unguarded by default.
 */
export function guardHostCallbacks<T extends object>(callbacks: T): T {
  const guarded: Record<string, unknown> = {}
  for (const [name, value] of Object.entries(callbacks)) {
    guarded[name] =
      typeof value === 'function'
        ? guardOne(name, value as HostCallback)
        : value
  }
  return guarded as T
}

function guardOne(name: string, callback: HostCallback): HostCallback {
  return (...args) => {
    try {
      const result = callback(...args)
      // The callback types are all void, but nothing stops a host passing an
      // `async` function, whose rejection no try/catch here would see.
      if (result instanceof Promise) {
        return result.catch((error: unknown) => warn(name, error))
      }
      return result
    } catch (error) {
      warn(name, error)
      return undefined
    }
  }
}

function warn(name: string, error: unknown): void {
  logger.warn('connect-rn:host_callback_threw', {
    callback: name,
    error: error instanceof Error ? error.message : String(error),
  })
}
